import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock3,
  ExternalLink,
  History,
  Loader2,
  MapPin,
  PackageCheck,
  RefreshCw,
  Smartphone,
  UserRound,
  X,
  XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../config';
import ModalShell from './ModalShell';


const STATUS_LABELS = {
  pending_review: 'Требует проверки',
  changes_requested: 'Нужно уточнение',
  confirmed: 'Подтверждена',
  rejected: 'Отклонена',
  cancelled: 'Отменена'
};

const FILTERS = [
  { value: 'active', label: 'Новые' },
  { value: 'changes_requested', label: 'Уточнение' },
  { value: 'history', label: 'История' }
];

const statusClasses = (status) => ({
  pending_review: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200',
  changes_requested: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200',
  confirmed: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200',
  rejected: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200',
  cancelled: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
}[status] || 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300');

const formatMoney = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;
const formatDate = (value) => {
  if (!value) return 'Дата не указана';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const customerName = (request) => {
  const customer = request?.customer || {};
  return [customer.first_name, customer.last_name].filter(Boolean).join(' ') || 'Клиент приложения';
};

const ClientOrderModerationPanel = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('active');
  const [selectedId, setSelectedId] = useState(null);
  const [openedRequest, setOpenedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [note, setNote] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupSlot, setPickupSlot] = useState('');

  const apiRequest = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_URL}/api/client-intake/v1${path}`, {
      ...options,
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        ...(options.headers || {})
      }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const detail = payload?.detail;
      throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Не удалось выполнить действие');
    }
    return payload;
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const payload = await apiRequest('/requests?review_status=&limit=200');
      setRequests(Array.isArray(payload) ? payload : []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load({ silent: true }), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const visibleRequests = useMemo(() => requests.filter((request) => {
    if (filter === 'active') return request.request_status === 'pending_review';
    if (filter === 'changes_requested') return request.request_status === 'changes_requested';
    return ['confirmed', 'rejected', 'cancelled'].includes(request.request_status);
  }), [filter, requests]);
  const counts = useMemo(() => ({
    active: requests.filter((row) => row.request_status === 'pending_review').length,
    changes_requested: requests.filter((row) => row.request_status === 'changes_requested').length,
    history: requests.filter((row) => ['confirmed', 'rejected', 'cancelled'].includes(row.request_status)).length
  }), [requests]);
  const selectedRequest = selectedId ? openedRequest : null;
  const latestRequest = requests.find((row) => Number(row.request_id || row.id) === Number(selectedId));
  const requestChanged = selectedRequest && latestRequest && (
    latestRequest.revision !== selectedRequest.revision || latestRequest.request_status !== selectedRequest.request_status
  );

  const openRequest = (request) => {
    setSelectedId(request.request_id || request.id);
    setOpenedRequest(request);
    setNote('');
    setPickupDate(String(request.pickup_date || '').slice(0, 10));
    setPickupSlot(request.pickup_time_slot || '');
    setError('');
    setNotice('');
  };

  const moderate = async (action) => {
    if (!selectedRequest) return;
    if (requestChanged) {
      setError('Загрузите актуальную версию заявки перед проверкой');
      return;
    }
    if (['request_changes', 'reject'].includes(action) && note.trim().length < 3) {
      setError('Укажите причину или вопрос клиенту');
      return;
    }
    setActionLoading(action);
    setError('');
    setNotice('');
    try {
      const updated = await apiRequest(`/requests/${selectedRequest.request_id || selectedRequest.id}/moderate`, {
        method: 'POST',
        body: JSON.stringify({
          action,
          expected_revision: selectedRequest.revision || 1,
          note: note.trim() || null,
          pickup_date: action === 'confirm' && !selectedRequest.self_delivery ? pickupDate || null : null,
          pickup_time_slot: action === 'confirm' && !selectedRequest.self_delivery ? pickupSlot || null : null
        })
      });
      setRequests((current) => current.map((row) => (
        Number(row.request_id || row.id) === Number(updated.request_id || updated.id) ? updated : row
      )));
      setOpenedRequest(updated);
      setNote('');
      setNotice(action === 'confirm'
        ? `Заявка подтверждена. Создан заказ №${updated.order_number}`
        : action === 'request_changes' ? 'Клиенту отправлен запрос на уточнение' : 'Заявка отклонена');
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setActionLoading('');
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-gray-900">
      <div className="flex flex-col gap-3 border-b border-gray-100 p-4 dark:border-gray-800 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"><Smartphone className="h-5 w-5" /></span>
          <div>
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Заявки из клиентского приложения</h2>
            <p className="mt-1 text-xs text-gray-500">До подтверждения это отдельная заявка без номера и без записи в журнале заказов.</p>
          </div>
        </div>
        <button type="button" onClick={() => load()} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Обновить</button>
      </div>

      <div className="flex gap-2 overflow-x-auto p-3">
        {FILTERS.map((item) => (
          <button type="button" key={item.value} onClick={() => setFilter(item.value)} className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${filter === item.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
            {item.label}<span className={`rounded-full px-1.5 py-0.5 text-[10px] ${filter === item.value ? 'bg-white/20' : 'bg-white dark:bg-gray-900'}`}>{counts[item.value]}</span>
          </button>
        ))}
      </div>

      {error && !selectedRequest && <div className="mx-3 mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center p-8 text-sm text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Загружаем заявки…</div>
      ) : visibleRequests.length ? (
        <div className="grid gap-2 border-t border-gray-100 p-3 dark:border-gray-800 lg:grid-cols-2">
          {visibleRequests.map((request) => (
            <button type="button" key={request.request_id || request.id} onClick={() => openRequest(request)} className="flex items-center gap-3 rounded-2xl border border-gray-200 p-3 text-left transition hover:border-blue-300 hover:bg-blue-50/30 dark:border-gray-700 dark:hover:border-blue-800 dark:hover:bg-blue-950/20">
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border ${statusClasses(request.request_status)}`}>
                {request.request_status === 'confirmed' ? <PackageCheck className="h-5 w-5" /> : request.request_status === 'pending_review' ? <Clock3 className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2"><strong className="truncate text-sm text-gray-900 dark:text-white">Заявка #{request.request_id || request.id} · {customerName(request)}</strong><small className="shrink-0 text-xs font-bold text-blue-600 dark:text-blue-400">{formatMoney(request.estimated_total || request.total)}</small></span>
                <span className="mt-1 flex items-center gap-1 text-xs text-gray-500"><MapPin className="h-3 w-3" />{request.department?.name || 'Подразделение не определено'} · {formatDate(request.created_at)}</span>
                <span className="mt-2 flex items-center gap-2"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusClasses(request.request_status)}`}>{STATUS_LABELS[request.request_status] || request.status_label}</span><span className="text-[10px] font-semibold text-gray-400">Версия {request.revision || 1}</span></span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
            </button>
          ))}
        </div>
      ) : (
        <div className="border-t border-gray-100 p-8 text-center dark:border-gray-800"><CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" /><h3 className="mt-3 text-sm font-semibold text-gray-900 dark:text-white">В этом разделе заявок нет</h3><p className="mt-1 text-xs text-gray-500">Новые заявки появятся здесь и не попадут в общий журнал до подтверждения.</p></div>
      )}

      <ModalShell open={Boolean(selectedRequest)} onClose={() => setSelectedId(null)} variant="window" panelClassName="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
        {selectedRequest && (
          <>
            <header className="flex items-start justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
              <div><div className="text-xs font-semibold uppercase tracking-wide text-blue-600">Клиентская заявка #{selectedRequest.request_id || selectedRequest.id} · версия {selectedRequest.revision || 1}</div><h3 className="mt-1 font-bold text-gray-900 dark:text-white">{customerName(selectedRequest)}</h3><p className="mt-1 text-xs text-gray-500">{selectedRequest.customer?.phone || 'Телефон не указан'} · {formatDate(selectedRequest.created_at)}</p></div>
              <button type="button" onClick={() => setSelectedId(null)} className="rounded-xl p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"><X className="h-5 w-5" /></button>
            </header>

            <div className="space-y-4 p-4">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <InfoCard label="Статус" value={STATUS_LABELS[selectedRequest.request_status] || selectedRequest.status_label} />
                <InfoCard label="Предварительно" value={formatMoney(selectedRequest.estimated_total || selectedRequest.total)} />
                <InfoCard label="Подразделение" value={selectedRequest.department?.name || '—'} />
                <InfoCard label="Позиций" value={selectedRequest.items?.filter((item) => !item.is_additional).length || 0} />
              </div>

              <div className="rounded-2xl border border-gray-200 dark:border-gray-700">
                <div className="border-b border-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-800">Состав заявки</div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {(selectedRequest.items || []).map((item) => (
                    <div key={item.id} className={`flex items-center justify-between gap-3 px-3 py-2.5 ${item.is_additional ? 'pl-7' : ''}`}>
                      <div className="min-w-0"><div className="truncate text-sm font-medium text-gray-900 dark:text-white">{item.is_additional ? `+ ${item.name}` : item.name}</div><div className="text-xs text-gray-500">{item.quantity || 1} {item.unit || 'шт.'}{item.area ? ` · ${item.area} м²` : ''}{item.weight ? ` · ${item.weight} кг` : ''}</div></div>
                      <strong className="shrink-0 text-sm text-gray-900 dark:text-white">{formatMoney(item.total)}</strong>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-xl bg-gray-50 p-3 dark:bg-gray-800"><div className="flex items-center gap-1 text-[11px] text-gray-500"><MapPin className="h-3 w-3" />Адрес</div><div className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{selectedRequest.self_delivery ? 'Самопривоз' : selectedRequest.address || '—'}</div></div>
                <div className="rounded-xl bg-gray-50 p-3 dark:bg-gray-800"><div className="flex items-center gap-1 text-[11px] text-gray-500"><CalendarDays className="h-3 w-3" />Желаемое время</div><div className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{selectedRequest.pickup_date || '—'} · {selectedRequest.pickup_time_slot || '—'}</div></div>
              </div>

              {selectedRequest.notes && <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-800 dark:bg-gray-800 dark:text-gray-200">Комментарий клиента: {selectedRequest.notes}</div>}
              {requestChanged && <div role="status" className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">Заявка изменилась после открытия. <button type="button" className="font-semibold underline" onClick={() => openRequest(latestRequest)}>Загрузить актуальные данные</button></div>}
              {(selectedRequest.revisions || []).length > 0 && (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-800"><History className="h-4 w-4" />История версий</div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {[...(selectedRequest.revisions || [])].reverse().map((revision) => (
                      <div key={revision.id || revision.version} className="px-3 py-3">
                        <div className="flex flex-wrap items-center justify-between gap-2"><div className="text-sm font-bold text-gray-900 dark:text-white">Версия {revision.version}</div><div className="flex items-center gap-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusClasses(revision.review_status)}`}>{STATUS_LABELS[revision.review_status] || revision.review_status}</span><span className="text-[10px] text-gray-400">{formatDate(revision.submitted_at)}</span></div></div>
                        <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">{(revision.changes || []).map((change, index) => <li key={`${revision.version}-${index}`} className="flex gap-2"><span className="text-blue-500">•</span><span>{change}</span></li>)}</ul>
                        {revision.moderation_note && <div className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">Результат проверки: {revision.moderation_note}</div>}
                        {revision.snapshot && <details className="mt-2 text-xs text-gray-600 dark:text-gray-300"><summary className="cursor-pointer font-semibold">Состав и условия этой версии · {formatMoney(revision.requested_total)}</summary><div className="mt-2 space-y-1"><div>{revision.snapshot.department} · {revision.snapshot.self_delivery ? 'Самопривоз' : revision.snapshot.address}</div><div>{revision.snapshot.pickup_date} {revision.snapshot.pickup_time_slot}</div>{(revision.snapshot.items || []).map((item, index) => <div key={index}>{item}</div>)}{revision.snapshot.notes && <div>Комментарий: {revision.snapshot.notes}</div>}</div></details>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {['pending_review', 'changes_requested'].includes(selectedRequest.request_status) && (
                <>
                  {!selectedRequest.self_delivery && <div className="grid gap-2 sm:grid-cols-2"><label className="text-xs font-medium text-gray-600 dark:text-gray-300">Дата забора<input type="date" value={pickupDate} onChange={(event) => setPickupDate(event.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" /></label><label className="text-xs font-medium text-gray-600 dark:text-gray-300">Интервал<input value={pickupSlot} onChange={(event) => setPickupSlot(event.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" /></label></div>}
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">Комментарий клиенту<textarea value={note} onChange={(event) => setNote(event.target.value)} rows="2" className="mt-1 w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" /></label>
                </>
              )}

              {selectedRequest.moderation_note && <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200">Комментарий: {selectedRequest.moderation_note}</div>}
              {error && <div className="flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200"><AlertTriangle className="h-4 w-4" />{error}</div>}
              {notice && <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200"><CheckCircle2 className="h-4 w-4" />{notice}</div>}

              {['pending_review', 'changes_requested'].includes(selectedRequest.request_status) ? (
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={Boolean(actionLoading)} onClick={() => moderate('confirm')} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{actionLoading === 'confirm' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />}Подтвердить и создать заказ</button>
                  <button type="button" disabled={Boolean(actionLoading)} onClick={() => moderate('request_changes')} className="inline-flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800 disabled:opacity-50 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"><Clock3 className="h-4 w-4" />Уточнить</button>
                  <button type="button" disabled={Boolean(actionLoading)} onClick={() => moderate('reject')} className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-700 disabled:opacity-50 dark:border-red-900 dark:text-red-300"><XCircle className="h-4 w-4" />Отклонить</button>
                </div>
              ) : selectedRequest.worker_order_id && (
                <button type="button" onClick={() => navigate(`/order/${selectedRequest.worker_order_id}`)} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white"><ExternalLink className="h-4 w-4" />Открыть заказ №{selectedRequest.order_number}</button>
              )}
            </div>
          </>
        )}
      </ModalShell>
    </section>
  );
};

const InfoCard = ({ label, value }) => (
  <div className="rounded-xl bg-gray-50 p-3 dark:bg-gray-800">
    <div className="flex items-center gap-1 text-[11px] text-gray-500"><UserRound className="h-3 w-3" />{label}</div>
    <div className="mt-1 truncate text-sm font-bold text-gray-900 dark:text-white">{value}</div>
  </div>
);

export default ClientOrderModerationPanel;
